import { useEffect, useState, useMemo, useCallback } from 'react';
import type { CreateProjectInviteContext } from '@scale/genai-compass-shared/cards/create_project_invite';
import { useCompassCard } from '@scale/compass-ui/store';
import { columnsFromRecord } from '@scale/compass-shared/utils';
import { getWorkerIdColumn } from 'react-scale/corp/views/DataCompassCardsV2/action/UpdateMetadataCard/utils';
import { VALID_DURATION_DAYS } from 'react-scale/corp/views/CorpProjectGroupManager/views/ThroughputTabs/ForecastComponents/OnboardingComponents/shared/projectInviteConstants';

/**
 * Calculates an expiration date from a number of days
 */
function calculateExpiresAt(days: number): string {
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + days);
  return expiresAt.toISOString();
}

/**
 * Validates that expiresAt is a valid future date
 */
function isValidExpiresAt(expiresAt: string): boolean {
  const date = new Date(expiresAt);
  return !isNaN(date.getTime()) && date > new Date();
}

const useCreateProjectInviteCard = (index: number) => {
  const { card, updateCardContext, updateCardCanSubmit } = useCompassCard(index);
  const { retrievedOutput } = useCompassCard(index - 1);
  const context = card.defaultContext as CreateProjectInviteContext;

  const data = useMemo(
    () => retrievedOutput?.response?.data ?? [],
    [retrievedOutput?.response?.data],
  );

  // Get columns from the previous card's output
  const availableColumns = columnsFromRecord(retrievedOutput?.response?.data) || [];

  const workerIdColumn = getWorkerIdColumn(retrievedOutput);
  const [numWorkers, setNumWorkers] = useState(0);

  /**
   * Validates the card and updates the canSubmit state
   * Can only submit if:
   * - projectId is provided
   * - columnIdWithWorkerIds is provided and has workers
   * - expiresAt is a valid future date
   */
  const validateFieldsAndMarkCanSubmit = useCallback(
    (updatedContext: CreateProjectInviteContext, updatedNumWorkers?: number) => {
      const hasProject = Boolean(updatedContext.projectId?.trim());
      const hasWorkers = (updatedNumWorkers ?? numWorkers) > 0;
      const hasValidExpiration = isValidExpiresAt(updatedContext.expiresAt);

      updateCardCanSubmit(hasProject && hasWorkers && hasValidExpiration);
    },
    [numWorkers, updateCardCanSubmit],
  );

  const setProjectId = (projectId: string) => {
    const updatedContext: CreateProjectInviteContext = {
      ...context,
      projectId,
    };
    updateCardContext(updatedContext);
    validateFieldsAndMarkCanSubmit(updatedContext);
  };

  const setExpiresAt = (expiresAt: string) => {
    const updatedContext: CreateProjectInviteContext = {
      ...context,
      expiresAt,
    };
    updateCardContext(updatedContext);
    validateFieldsAndMarkCanSubmit(updatedContext);
  };

  /**
   * Convenience setter that takes days and converts to expiresAt
   */
  const setExpirationDays = (days: number) => {
    setExpiresAt(calculateExpiresAt(days));
  };

  const setBatchName = (batchName: string | undefined) => {
    const updatedContext: CreateProjectInviteContext = {
      ...context,
      batchName: batchName || undefined,
    };
    updateCardContext(updatedContext);
  };

  const setInsertOrder = (insertOrder: number | undefined) => {
    const updatedContext: CreateProjectInviteContext = {
      ...context,
      insertOrder,
    };
    updateCardContext(updatedContext);
  };

  const setUseLatestBatchExpiresAt = (useLatestBatchExpiresAt: boolean) => {
    const updatedContext: CreateProjectInviteContext = {
      ...context,
      useLatestBatchExpiresAt,
    };
    updateCardContext(updatedContext);
  };

  const setDryRun = (dryRun: boolean) => {
    const updatedContext: CreateProjectInviteContext = {
      ...context,
      dryRun,
    };
    updateCardContext(updatedContext);
  };

  const setColumnIdWithWorkerIds = useCallback(
    (columnIdWithWorkerIds: string) => {
      const updatedContext: CreateProjectInviteContext = {
        ...context,
        columnIdWithWorkerIds,
      };
      updateCardContext(updatedContext);

      if (!columnIdWithWorkerIds) {
        setNumWorkers(0);
        updateCardCanSubmit(false);
        return;
      }

      const workerRows = data.filter(row => row[columnIdWithWorkerIds]);
      setNumWorkers(workerRows.length);
      validateFieldsAndMarkCanSubmit(updatedContext, workerRows.length);
    },
    [context, data, updateCardContext, updateCardCanSubmit, validateFieldsAndMarkCanSubmit],
  );

  // Auto-select worker ID column if available
  useEffect(() => {
    if (workerIdColumn && !context.columnIdWithWorkerIds) {
      setColumnIdWithWorkerIds(workerIdColumn);
    }
  }, [workerIdColumn, context.columnIdWithWorkerIds, setColumnIdWithWorkerIds]);

  // Calculate current expiration days from expiresAt for UI display
  const expirationDays = useMemo(() => {
    const expiresAtDate = new Date(context.expiresAt);
    const now = new Date();
    const diffTime = expiresAtDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(1, Math.min(3, diffDays)); // Clamp to 1-3
  }, [context.expiresAt]);

  return {
    // Context values
    projectId: context.projectId,
    expiresAt: context.expiresAt,
    expirationDays, // Computed from expiresAt for UI
    batchName: context.batchName,
    insertOrder: context.insertOrder,
    columnIdWithWorkerIds: context.columnIdWithWorkerIds,
    useLatestBatchExpiresAt: context.useLatestBatchExpiresAt ?? false,
    dryRun: context.dryRun ?? false,

    // Setters
    setProjectId,
    setExpiresAt,
    setExpirationDays, // Convenience setter that converts days to expiresAt
    setBatchName,
    setInsertOrder,
    setColumnIdWithWorkerIds,
    setUseLatestBatchExpiresAt,
    setDryRun,

    // Helper data
    availableColumns,
    numWorkers,
    validDurationDays: VALID_DURATION_DAYS,
    card,
  };
};

export default useCreateProjectInviteCard;
